import { useEffect, useState } from "react";
import { Search, ShoppingBag, User, Copy } from "lucide-react";
import { toast } from "sonner";
import { api, fmtDate, errText } from "../lib/api";

const OrderCard = ({ o, onTrack }) => (
  <div className="card-tactile p-2.5 text-xs space-y-1" data-testid={`order-card-${o.id}`}>
    <div className="flex justify-between font-bold font-heading">
      <span>#{o.number}</span>
      <span className={`px-1.5 rounded border border-[#131E3A] uppercase text-[10px] ${o.status === "completed" ? "bg-[#D4F0F0]" : o.status === "cancelled" ? "bg-[#FFD1DC]" : "bg-[#FDF0A0]"}`}>{o.status}</span>
    </div>
    <div className="text-slate-600">{fmtDate(o.date)} · ₹{o.total} · {o.payment_method || ""}</div>
    {o.tracking && <div data-testid={`order-tracking-${o.id}`} className="font-bold bg-[#D4F0F0] border border-[#131E3A] rounded px-1.5 py-0.5 inline-block">🚚 {o.tracking}</div>}
    {(o.items || []).slice(0, 3).map((i, idx) => <div key={idx} className="truncate">• {i.name} ×{i.qty}</div>)}
    {(o.items || []).length > 3 && <div className="text-slate-500">+{o.items.length - 3} more items</div>}
    {onTrack && <button data-testid={`track-button-${o.id}`} className="btn-outline-tactile px-2 py-0.5 text-[11px] mt-1" onClick={() => onTrack(o.id)}>🚚 Track shipment</button>}
  </div>
);

const fmtEpoch = (ms) => ms ? new Date(ms).toLocaleString("en-IN", { timeZone: "Asia/Kolkata", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "";

const TrackTimeline = ({ track, loading, onShare }) => {
  if (loading) return <div className="text-xs text-slate-500 text-center py-2">Fetching tracking…</div>;
  if (!track) return null;
  if (!track.success) return <div data-testid="tracking-timeline" className="card-sticky p-3 text-xs" style={{ background: "var(--sd-pink)" }}>No tracking info available for #{track.for_order}{track.error ? ` — ${track.error}` : ""}</div>;
  return (
    <div data-testid="tracking-timeline" className="card-sticky p-3 text-xs space-y-2">
      <div className="flex items-center justify-between gap-2">
        <div className="font-heading font-bold">🚚 Tracking · #{track.order_id}</div>
        {onShare && <button data-testid="share-tracking-button" className="btn-tactile px-2 py-1 text-[11px]" onClick={onShare}>Share to chat</button>}
      </div>
      <div className="flex flex-wrap gap-1.5">
        {track.current_status && <span className="font-bold bg-[#D4F0F0] border border-[#131E3A] rounded px-1.5 py-0.5">{track.current_status}</span>}
        {track.carrier && <span className="border border-[#131E3A] rounded px-1.5 py-0.5 bg-white">{track.carrier}</span>}
        {track.tracking_code && <span className="border border-[#131E3A] rounded px-1.5 py-0.5 bg-white select-text">{track.tracking_code}</span>}
      </div>
      <div className="space-y-1.5 max-h-56 overflow-y-auto pr-1">
        {(track.events || []).map((e, i) => (
          <div key={i} className={`border-l-2 pl-2 ${i === 0 ? "border-[#131E3A]" : "border-slate-300"}`}>
            <div className={`${i === 0 ? "font-bold" : "font-semibold"}`}>{e.status}{e.location ? ` · ${e.location}` : ""}</div>
            <div className="text-slate-500 text-[10px]">{fmtEpoch(e.time)}{e.desc ? ` — ${e.desc}` : ""}</div>
          </div>
        ))}
        {(track.events || []).length === 0 && <div className="text-slate-500">No tracking events yet.</div>}
      </div>
    </div>
  );
};

export default function ContextPanel({ detail, onCustomerUpdate }) {
  const { customer, past_conversations } = detail;
  const [tab, setTab] = useState("customer");
  const [notes, setNotes] = useState(customer?.notes || "");
  const [tagInput, setTagInput] = useState("");
  const [orders, setOrders] = useState(null);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const [q, setQ] = useState("");
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [track, setTrack] = useState(null);
  const [trackLoading, setTrackLoading] = useState(false);

  const loadTrack = async (orderId) => {
    setTrackLoading(true); setTrack(null);
    try {
      const { data } = await api.get(`/track/${orderId}`);
      setTrack({ ...data, for_order: orderId });
    } catch (e) { setTrack({ success: false, for_order: orderId, error: errText(e) }); }
    setTrackLoading(false);
  };

  useEffect(() => {
    setNotes(customer?.notes || "");
    setOrders(null); setSearchResults(null); setTrack(null);
    if (!customer) return;
    api.get(`/customers/${customer.id}/history`).then((r) => setHistory(r.data));
    setOrdersLoading(true);
    api.get(`/woo/customer-orders/${customer.id}`)
      .then((r) => setOrders(r.data.orders))
      .catch(() => setOrders([]))
      .finally(() => setOrdersLoading(false));
  }, [customer?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!customer) return null;

  const saveCustomer = async (patch) => {
    try {
      const { data } = await api.put(`/customers/${customer.id}`, patch);
      onCustomerUpdate(data);
    } catch (e) { toast.error(errText(e)); }
  };

  const addTag = () => {
    if (!tagInput.trim()) return;
    saveCustomer({ tags: [...(customer.tags || []), tagInput.trim()] });
    setTagInput("");
  };

  const doSearch = async (e) => {
    e.preventDefault();
    if (!q.trim()) return;
    setSearching(true);
    setTrack(null);
    try {
      const { data } = await api.get("/woo/search", { params: { q: q.trim() } });
      setSearchResults(data.orders);
      if (/^\d{5,9}$/.test(q.trim())) loadTrack(q.trim());
    } catch (err) { toast.error(errText(err)); }
    setSearching(false);
  };

  const badge = orders === null ? null : orders.length > 0
    ? `${orders.length} order${orders.length > 1 ? "s" : ""} · last on ${fmtDate(orders[0]?.date)}`
    : "No orders yet";

  return (
    <div className="w-80 shrink-0 border-l-2 border-[#131E3A] flex flex-col overflow-hidden">
      <div className="flex border-b-2 border-[#131E3A]">
        {[{ id: "customer", label: "Customer Info", icon: User }, { id: "orders", label: "Order Info", icon: ShoppingBag }].map((t) => (
          <button key={t.id} data-testid={`context-tab-${t.id}`} onClick={() => setTab(t.id)}
            className={`flex-1 py-2 text-xs font-bold font-heading uppercase tracking-wider flex items-center justify-center gap-1.5 ${tab === t.id ? "bg-[#131E3A] text-white" : "hover:bg-[#FDF0A0]"}`}>
            <t.icon size={13} /> {t.label}
          </button>
        ))}
      </div>
      <div className="flex-1 overflow-y-auto p-3 space-y-4">
        {tab === "customer" ? (
          <>
            <div className="card-sticky p-3" style={{ userSelect: "text" }}>
              <div className="font-heading font-bold">{customer.name}</div>
              <div className="text-xs text-slate-700 mt-1 space-y-1">
                {customer.email && (
                  <div data-testid="customer-email" className="flex items-center gap-1.5">
                    <span className="select-text">✉ {customer.email}</span>
                    <button data-testid="copy-email-button" title="Copy email" onClick={() => { navigator.clipboard.writeText(customer.email); toast.success("Email copied"); }}><Copy size={11} /></button>
                  </div>
                )}
                {customer.phone && (
                  <div data-testid="customer-phone" className="flex items-center gap-1.5">
                    <span className="select-text">☎ {customer.phone}</span>
                    <button data-testid="copy-phone-button" title="Copy phone" onClick={() => { navigator.clipboard.writeText(customer.phone); toast.success("Phone copied"); }}><Copy size={11} /></button>
                  </div>
                )}
                <div className="font-script text-base text-amber-800 pt-1">{past_conversations} past conversation{past_conversations !== 1 ? "s" : ""}</div>
              </div>
            </div>
            <div>
              <p className="sd-label mb-1.5">Tags</p>
              <div className="flex flex-wrap gap-1.5 mb-2">
                {(customer.tags || []).map((t) => (
                  <span key={t} className="text-[11px] font-bold bg-[#D0E8FF] border border-[#131E3A] rounded-full px-2 py-0.5">
                    {t} <button data-testid={`remove-tag-${t}`} onClick={() => saveCustomer({ tags: customer.tags.filter((x) => x !== t) })} className="ml-0.5">×</button>
                  </span>
                ))}
              </div>
              <div className="flex gap-1.5">
                <input data-testid="tag-input" className="sd-input py-1.5 text-xs" placeholder="Add tag…" value={tagInput} onChange={(e) => setTagInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addTag()} />
                <button data-testid="add-tag-button" className="btn-outline-tactile px-2.5 text-xs" onClick={addTag}>+</button>
              </div>
            </div>
            <div>
              <p className="sd-label mb-1.5">Agent Notes</p>
              <textarea data-testid="notes-textarea" className="sd-input text-xs min-h-[80px]" style={{ background: "var(--sd-sticky)" }} value={notes} onChange={(e) => setNotes(e.target.value)} onBlur={() => notes !== customer.notes && saveCustomer({ notes })} placeholder="Internal notes about this customer…" />
            </div>
            <div>
              <p className="sd-label mb-1.5">Conversation History</p>
              <div className="space-y-1.5">
                {history.map((h) => (
                  <div key={h.id} className="card-tactile p-2 text-xs">
                    <div className="flex justify-between font-bold"><span>{h.ticket_number}</span><span className={`px-1 rounded border border-[#131E3A] text-[10px] ${h.status === "open" ? "bg-[#D4F0F0]" : "bg-[#E8DFF5]"}`}>{h.status}</span></div>
                    <div className="text-slate-500 truncate">{fmtDate(h.created_at)} · {h.last_message_preview}</div>
                  </div>
                ))}
              </div>
            </div>
          </>
        ) : (
          <>
            {ordersLoading ? (
              <div className="text-center text-sm text-slate-500 py-4">Checking WooCommerce orders…</div>
            ) : (
              badge && <div data-testid="orders-badge" className={`card-sticky p-2.5 text-center font-heading font-bold text-sm ${orders?.length ? "" : "opacity-80"}`} style={orders?.length ? {} : { background: "var(--sd-pink)" }}>{badge}</div>
            )}
            <div className="space-y-2">{(orders || []).map((o) => <OrderCard key={o.id} o={o} onTrack={loadTrack} />)}</div>
            <div>
              <p className="sd-label mb-1.5">Manual Order Search</p>
              <form onSubmit={doSearch} className="flex gap-1.5">
                <input data-testid="order-search-input" className="sd-input py-1.5 text-xs" placeholder="Order # or phone…" value={q} onChange={(e) => setQ(e.target.value)} />
                <button data-testid="order-search-button" className="btn-tactile px-2.5" disabled={searching}><Search size={13} /></button>
              </form>
              {searching && <p className="text-xs text-slate-500 mt-2">Searching… (phone lookups can take up to a minute)</p>}
              <div className="mt-2"><TrackTimeline track={track} loading={trackLoading} onShare={async () => {
                try {
                  await api.post(`/conversations/${detail.conversation.id}/share-tracking`, { order_id: String(track.order_id) });
                  toast.success("Tracking image shared to chat");
                } catch (e) { toast.error(errText(e)); }
              }} /></div>
              {searchResults && (
                <div className="space-y-2 mt-2">
                  {searchResults.length === 0 && <p data-testid="search-no-results" className="text-xs text-slate-500">No orders found.</p>}
                  {searchResults.map((o) => <OrderCard key={o.id} o={o} onTrack={loadTrack} />)}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
